// P14 — Host global que renderiza modais 402 disparados pelo interceptor.
//
// Escuta o custom event `quota:error` emitido por `quotaErrorHandler.ts`
// (que por sua vez é chamado pelo interceptor 402 do axios). Decide entre
// `QuotaBlockedModal` (tokens estourados — hard block, Q4) e
// `QuotaIncreaseRequestModal` (cota não-tokens: agentes/arquivos/tamanho).
// Modelo não permitido cai num toast simples no caller.
//
// Montado UMA vez no AppLayout, evita prop drilling e garante singleton de
// modal (combinado com a flag de `quotaErrorHandler.ts`).

import { FC, useEffect, useState } from 'react';
import quotaService from '@/services/quota.service';
import { useToast } from '@/contexts/ToastContext';
import { MyQuota, QuotaErrorResponse } from '@/types/agents';
import {
  QUOTA_ERROR_EVENT,
  QuotaErrorEventDetail,
  clearActiveQuotaModal,
} from '@/utils/quotaErrorHandler';
import QuotaBlockedModal from './QuotaBlockedModal';
import QuotaIncreaseRequestModal from './QuotaIncreaseRequestModal';

const QuotaErrorHost: FC = () => {
  const { showToast } = useToast();
  const [blocked, setBlocked] = useState<QuotaErrorResponse | null>(null);
  const [requestPayload, setRequestPayload] = useState<QuotaErrorResponse | null>(null);
  const [quota, setQuota] = useState<MyQuota | null>(null);

  useEffect(() => {
    const handler = (ev: Event) => {
      const detail = (ev as CustomEvent<QuotaErrorEventDetail>).detail;
      if (!detail) return;
      if (detail.kind === 'toast') {
        showToast(detail.payload.message, 'error');
        return;
      }
      if (detail.kind === 'blocked') {
        setBlocked(detail.payload);
        return;
      }
      // 'request' → precisa da cota pra abrir o modal de pedir mais
      quotaService
        .getMyQuota()
        .then((q) => {
          setQuota(q);
          setRequestPayload(detail.payload);
        })
        .catch(() => {
          showToast(detail.payload.message, 'error');
          clearActiveQuotaModal();
        });
    };
    window.addEventListener(QUOTA_ERROR_EVENT, handler);
    return () => window.removeEventListener(QUOTA_ERROR_EVENT, handler);
  }, [showToast]);

  const closeBlocked = () => {
    setBlocked(null);
    clearActiveQuotaModal();
  };
  const closeRequest = () => {
    setRequestPayload(null);
    clearActiveQuotaModal();
  };

  return (
    <>
      {blocked && <QuotaBlockedModal error={blocked} onClose={closeBlocked} />}
      {requestPayload && quota && (
        <QuotaIncreaseRequestModal
          quota={quota}
          onClose={closeRequest}
          onSubmitted={closeRequest}
        />
      )}
    </>
  );
};

export default QuotaErrorHost;
